import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.ts';
import { letolt } from '../letoltes.ts';
import { ft } from '../formazas.ts';

/**
 * Napló. (3.0 / 2.)
 *
 * Minden adatot változtató vagy pénzt érintő művelet itt látszik: ki,
 * mikor, mit csinált, mi volt előtte és mi lett utána. A **feloldás, a
 * törlés és a jogosultságváltozás kiemelve** — ezeket szoktuk keresni.
 *
 * A napló nem szerkeszthető és nem törölhető, adminisztrátornak sem:
 * ezért nincs rajta egyetlen módosító gomb sem.
 */

interface NaploSor {
  id: number;
  idopont: string;
  felhasznalo: string;
  szerepkor: string | null;
  fajta: string;
  fajta_nev: string;
  kiemelt: boolean;
  mire: string;
  elotte: string;
  utana: string;
}

interface NaploValasz {
  sorok: NaploSor[];
  osszes: number;
  talalat: number;
  kiemelt: number;
  felhasznalok: string[];
  fajta_darabok: Record<string, number>;
  fajtak: Array<[string, string]>;
}

/** 2026-09-25T11:04:12.345Z → 2026.09.25. 11:04:12 */
function idoSzoveg(iso: string): string {
  const d = iso.slice(0, 10).replace(/-/g, '.');
  return `${d}. ${iso.slice(11, 19)}`;
}

export function Naplo() {
  const [kereses, setKereses] = useState('');
  const [keresesKesleltetve, setKeresesKesleltetve] = useState('');
  const [fajta, setFajta] = useState('mind');
  const [felhasznalo, setFelhasznalo] = useState('');
  const [mettol, setMettol] = useState('');
  const [meddig, setMeddig] = useState('');
  const [adat, setAdat] = useState<NaploValasz | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [toltes, setToltes] = useState(true);

  useEffect(() => {
    const idozito = setTimeout(() => setKeresesKesleltetve(kereses), 250);
    return () => clearTimeout(idozito);
  }, [kereses]);

  const parameterek = useCallback(() => {
    const p = new URLSearchParams();
    if (keresesKesleltetve) p.set('q', keresesKesleltetve);
    if (fajta !== 'mind') p.set('fajta', fajta);
    if (felhasznalo) p.set('felhasznalo', felhasznalo);
    if (mettol) p.set('mettol', mettol);
    if (meddig) p.set('meddig', meddig);
    return p.toString();
  }, [keresesKesleltetve, fajta, felhasznalo, mettol, meddig]);

  const betolt = useCallback(async () => {
    setToltes(true);
    try {
      setAdat(await api.get<NaploValasz>(`/api/naplo?${parameterek()}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A naplót nem sikerült betölteni.');
    } finally {
      setToltes(false);
    }
  }, [parameterek]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const szurok: Array<[string, string]> = [
    ['mind', 'Mind'],
    ['kiemelt', 'Kiemelt'],
    ...(adat?.fajtak ?? []),
  ];
  const darab = (kulcs: string) =>
    kulcs === 'mind'
      ? Object.values(adat?.fajta_darabok ?? {}).reduce((a, b) => a + b, 0)
      : kulcs === 'kiemelt'
        ? (adat?.kiemelt ?? 0)
        : (adat?.fajta_darabok[kulcs] ?? 0);

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Napló</span>
        <input
          className="qi"
          style={{ width: 200, fontSize: 12.5 }}
          placeholder="keresés…"
          value={kereses}
          onChange={(e) => setKereses(e.target.value)}
        />
        <select value={felhasznalo} onChange={(e) => setFelhasznalo(e.target.value)}>
          <option value="">Minden felhasználó</option>
          {(adat?.felhasznalok ?? []).map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={mettol}
          title="Ettől a naptól"
          onChange={(e) => setMettol(e.target.value)}
        />
        <input
          type="date"
          value={meddig}
          title="Eddig a napig"
          onChange={(e) => setMeddig(e.target.value)}
        />
        <div className="stats">
          <div>
            Bejegyzés<strong>{ft(adat?.osszes ?? 0)}</strong>
          </div>
          <div>
            Kiemelt<strong>{ft(adat?.kiemelt ?? 0)}</strong>
          </div>
        </div>
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}

      <div className="fchips">
        {szurok.map(([k, cimke]) => (
          <button
            key={k}
            className={`fchip${fajta === k ? ' on' : ''}`}
            onClick={() => setFajta(k)}
          >
            {cimke}
            <b>{ft(darab(k))}</b>
          </button>
        ))}
      </div>

      <div className="actbar" style={{ marginTop: 0 }}>
        <button className="btn sm" onClick={() => letolt(`/api/export/naplo.xlsx?${parameterek()}`)}>
          Excel
        </button>
        <span className="hint">
          A napló nem szerkeszthető és nem törölhető — adminisztrátornak sem.
          {adat && adat.talalat > adat.sorok.length
            ? ` A szűrésnek ${ft(adat.talalat)} bejegyzés felel meg, itt a legfrissebb ${ft(adat.sorok.length)} látszik; az Excelben több is.`
            : ''}
        </span>
      </div>

      <div className="matrix" style={{ maxHeight: 620, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th className="n">Időpont</th>
              <th>Felhasználó</th>
              <th>Művelet</th>
              <th>Mire vonatkozott</th>
              <th>Előtte</th>
              <th>Utána</th>
            </tr>
          </thead>
          <tbody>
            {(adat?.sorok ?? []).map((n) => (
              <tr key={n.id} className={n.kiemelt ? 'nplki' : undefined}>
                <td className="calc2" style={{ whiteSpace: 'nowrap' }}>
                  {idoSzoveg(n.idopont)}
                </td>
                <td className="nplfelh">
                  {n.felhasznalo}
                  <div>{n.szerepkor ?? ''}</div>
                </td>
                <td className="nplmuv">{n.kiemelt ? <b>{n.fajta_nev}</b> : n.fajta_nev}</td>
                <td style={{ padding: '7px 9px', fontSize: 12 }}>{n.mire}</td>
                <td className="calc2 nplert">{n.elotte || '·'}</td>
                <td className="calc2 nplert uj">{n.utana || '·'}</td>
              </tr>
            ))}
            {(adat?.sorok.length ?? 0) === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  {toltes ? 'Betöltés…' : 'Nincs bejegyzés.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
